import React, { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import './PaneCashBack.css';
import { dynamicRequest } from '../../utils/api-utils/api-utils';
import { RewardsCounterResponse } from '../../types/responses/RewardsCounter';
import { wrapTrademarkSuperscript } from '../../utils/cob-utils';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import { Title } from '../../importCL';

interface IPaneCashBackProps extends MappedComponentProperties {
    bottomText?: string;
    ctaOpenNewTab?: boolean;
    claimsCardHeadline?: string;
}

export const PaneCashBackEditConfig = {
    emptyLabel: 'PaneCashBack',
    isEmpty: (props: PropsWithChildren<IPaneCashBackProps>) => {
        return !props || !props.bottomText;
    },
};

const PaneCashBack = (props: PropsWithChildren<IPaneCashBackProps>): JSX.Element => {
    const [value, setValue] = useState(181028798);
    const [oldValue, setOldValue] = useState(181028798);
    const [incrementMax, setIncrementMax] = useState(10);

    const TIMER_DELAY_MIN: number = 5;
    const TIMER_DELAY_MAX: number = 10;

    const getFormattedCurrency = (val: number): string => {
        return `$${val.toLocaleString('en-US')}`;
    };

    /**
     * Queries mocks or production API to get reward counter data
     */
    const getValue = useCallback(async () => {
        const result = await dynamicRequest<RewardsCounterResponse>('rewards-counter', { headers: { 'Content-Type': 'text/xml' } });
        setValue(result.data.TotalBestowed ?? 181028798);
        setOldValue(result.data.TotalBestowed ?? 181028798)
        setIncrementMax(result.data.DisplayIncrement ?? 10);
    }, []);

    useEffect(() => {
        getValue().catch((err) => console.error(err));
    }, []);

    /**
     * This useEffect call watches for {value} in State
     * and creates a timer with a randomized interval for a configurable
     * amount of seconds.
     *
     * Currently, the 'addToCounter' method is taking the incrementMax variable
     * and setting the increment minimum to half of itself to give the counter
     * a more 'realistic' look.
     */
    useEffect(() => {
        const timerDelay = Math.floor(Math.random() * (TIMER_DELAY_MAX - TIMER_DELAY_MIN + 1) + TIMER_DELAY_MIN);
        const addToCounter = Math.floor(Math.random() * (incrementMax - (incrementMax / 2) + 1) + (incrementMax / 2));
        const timer = setTimeout(() => {
            const newValue = value + addToCounter;
            setValue(newValue);
            if (newValue - oldValue > 250) {
                setOldValue(newValue);
            }
        }, timerDelay * 1000);

        return () => {
            clearTimeout(timer);
        };
    }, [value]);

    return (
        <div id='pane-cash-back' className='PaneCashBack'>
            <Title tag='h3' className='PaneCashBack__text--dollar' align='center'>
                {getFormattedCurrency(value)}
            </Title>

            <span aria-atomic="true" aria-live="polite" id="cashback-announcement" className="sr-only">
                {`${props.claimsCardHeadline} updated to ${getFormattedCurrency(oldValue)}`}
            </span>

            <Title className='PaneCashBack__text--bottom' tag='h4' align='center'>
                {transformHTMLParserLinks(wrapTrademarkSuperscript(props.bottomText))}
            </Title>
        </div>
    );
};

export default PaneCashBack;
